import { Component } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { ModalClass } from '@app/core/class/modal.class';
import { AnnouncementModel } from '@app/core/model/announcement.model';
import { LedgerModel } from '@app/core/model/ledger.model';
import { UserModel } from '@app/core/model/user.model';
import { LedgerApiService } from '@app/core/service/api/ledger.api.service';
import { PsychologicalSurveyApiService } from '@app/core/service/api/psychologicalSurvey.api.service';
import { RecognitionApiService } from '@app/core/service/api/recognition.api.service';
import { TasksApiService } from '@app/core/service/api/tasks.api.service';
import { TenantService } from '@app/core/service/tenant.service';
import { ToastService } from '@app/core/service/toast.service';
import { WhatsNewService } from '@app/core/service/whats-new.service';
import { MongoStoredObject } from '@app/types/mongo-stored-object';
import { showEditRecognitionModal } from '@app/utils/modal';
import { IPsychologicalSurvey } from '@backend/models/types/psychological-survey';
import { IPsychologicalSurveyQuestion } from '@backend/models/types/psychological-survey-question';
import { UserRoles } from '@backend/models/types/user';
import {
  ActionSheetController,
  LoadingController,
  ModalController
} from '@ionic/angular';

@Component({
  selector: 'app-whats-new-modal',
  templateUrl: './whats-new-modal.component.html',
  styleUrls: ['./whats-new-modal.component.scss'],
  standalone: false
})
export class WhatsNewModalComponent extends ModalClass {
  protected announcementsIsLoaded = false;
  protected recognitionsIsLoaded = false;
  protected surveysIsLoaded = false;
  protected recognitionRemindersIsLoaded = false;

  protected announcements: {
    items: AnnouncementModel[];
    count: number;
    totalCount: number;
  } = { items: [], count: 0, totalCount: 0 };
  protected selectedAnnouncement: AnnouncementModel | null = null;
  protected selectedAnnouncementIndex = -1;

  protected recognitions: {
    items: LedgerModel[];
    count: number;
    totalCount: number;
  } = { items: [], count: 0, totalCount: 0 };

  protected surveys: {
    items: {
      survey: MongoStoredObject<IPsychologicalSurvey>;
      questions: MongoStoredObject<IPsychologicalSurveyQuestion>[];
    }[];
    count: number;
    totalCount: number;
  } = { items: [], count: 0, totalCount: 0 };
  protected selectedSurvey: {
    survey: MongoStoredObject<IPsychologicalSurvey>;
    questions: MongoStoredObject<IPsychologicalSurveyQuestion>[];
  } = null;

  protected recognitionReminders: {
    items: { title: string }[];
    count: number;
    totalCount: number;
  } = {
    items: [],
    count: 0,
    totalCount: 0
  };
  protected recognitionReminderButtons = [
    {
      id: 'send-recognition',
      text: 'Send Recognition'
    },
    {
      id: 'dismiss',
      text: 'Dismiss'
    }
  ];

  protected isAdmin: boolean = false;
  protected authenticatedUser: UserModel;
  protected tenantId: string = '';
  protected showRecognitionPoints: boolean = false;
  protected surveyPoints: number = 0;

  public constructor(
    private readonly _authenticationService: AuthenticationService,
    private readonly _ledgerApiService: LedgerApiService,
    private readonly _loadingCtrl: LoadingController,
    private readonly _toastService: ToastService,
    private readonly _whatsNewService: WhatsNewService,
    private readonly _modalCtrl: ModalController,
    private readonly _recognitionApiService: RecognitionApiService,
    private readonly _tenantService: TenantService,
    private readonly _actionSheetCtrl: ActionSheetController,
    private readonly _tasksApiService: TasksApiService,
    private readonly _psychologicalSurveyApiService: PsychologicalSurveyApiService
  ) {
    super('WhatsNewModalComponent');

    this._tenantService.tenant$.subscribe((tenant) => {
      this.tenantId = tenant._id;
      this.showRecognitionPoints = tenant.settings.recognition.showPoints;

      if (tenant.tasks?.dailySurvey) {
        this._tasksApiService
          .getTaskById({ path: { id: tenant.tasks.dailySurvey.toString() } })
          .subscribe((task) => {
            this.surveyPoints = task.points || 0;
          });
      }
    });

    this.authenticatedUser = this._authenticationService.user;
    this.isAdmin = this.authenticatedUser
      ? this.authenticatedUser.roles.includes(UserRoles.Admin)
      : false;

    this._whatsNewService.announcements$.subscribe((announcements) => {
      if (
        announcements.count === 0 &&
        this.recognitions.count === 0 &&
        this.recognitionsIsLoaded &&
        this.surveys.count === 0 &&
        this.surveysIsLoaded &&
        this.recognitionReminders.count === 0 &&
        this.recognitionRemindersIsLoaded
      ) {
        this._modalCtrl.dismiss(null, 'confirm', this._modalName);
      }

      this.announcements = announcements;
      this.announcementsIsLoaded = true;
    });
    this._whatsNewService.recognitions$.subscribe((recognitions) => {
      if (
        recognitions.count === 0 &&
        this.announcements.count === 0 &&
        this.announcementsIsLoaded &&
        this.surveys.count === 0 &&
        this.surveysIsLoaded &&
        this.recognitionReminders.count === 0 &&
        this.recognitionRemindersIsLoaded
      ) {
        this._modalCtrl.dismiss(null, 'confirm', this._modalName);
      }

      this.recognitions = recognitions;
      this.recognitionsIsLoaded = true;
    });
    this._whatsNewService.surveys$.subscribe((surveys) => {
      if (
        surveys.count === 0 &&
        this.recognitions.count === 0 &&
        this.recognitionsIsLoaded &&
        this.announcements.count === 0 &&
        this.announcementsIsLoaded &&
        this.recognitionReminders.count === 0 &&
        this.recognitionRemindersIsLoaded
      ) {
        this._modalCtrl.dismiss(null, 'confirm', this._modalName);
      }

      this.surveys = surveys;
      this.surveysIsLoaded = true;
    });
    this._whatsNewService.recognitionReminders$.subscribe(
      (recognitionReminders) => {
        if (
          recognitionReminders.count === 0 &&
          this.recognitions.count === 0 &&
          this.recognitionsIsLoaded &&
          this.announcements.count === 0 &&
          this.announcementsIsLoaded &&
          this.surveys.count === 0 &&
          this.surveysIsLoaded
        ) {
          this._modalCtrl.dismiss(null, 'confirm', this._modalName);
        }

        this.recognitionReminders = recognitionReminders;
        this.recognitionRemindersIsLoaded = true;
      }
    );
  }

  protected async readRecognition(recognition: LedgerModel, index: number) {
    const loading = await this._loadingCtrl.create({
      message: 'Loading...'
    });
    loading.present();

    this._ledgerApiService
      .readLedgerById({
        path: { id: recognition._id, tenantId: this.tenantId },
        body: { recipientId: recognition.recipient._id }
      })
      .subscribe(() => {
        loading.dismiss();
        this._toastService.presentToast(
          'The recognition was successfully marked as read!'
        );
        this.removeItemFromList(index, 'recognition', recognition._id);
      });
  }

  protected onAnnouncementClick(
    announcement: AnnouncementModel,
    index: number
  ) {
    if (!this.selectedAnnouncement) {
      this.selectedAnnouncement = announcement;
      this.selectedAnnouncementIndex = index;
    }
  }

  protected onSurveyClick(survey: {
    survey: MongoStoredObject<IPsychologicalSurvey>;
    questions: MongoStoredObject<IPsychologicalSurveyQuestion>[];
  }) {
    this.selectedSurvey = survey;
  }

  protected async onSurveySkip(
    survey: {
      survey: MongoStoredObject<IPsychologicalSurvey>;
      questions: MongoStoredObject<IPsychologicalSurveyQuestion>[];
    },
    index: number
  ) {
    const loading = await this._loadingCtrl.create({
      message: 'Skipping...'
    });
    await loading.present();

    this._psychologicalSurveyApiService
      .skipPsychologicalSurvey({ path: { surveyId: survey.survey._id } })
      .subscribe({
        next: () => {
          loading.dismiss();
          this.removeItemFromList(index, 'survey', survey.survey._id);
        },
        error: () => {
          loading.dismiss();
          this._toastService.presentToast('Unable to skip the survey');
        }
      });
  }

  protected onAnnouncementAcknowledged(event: { index: number; id: string }) {
    this.removeItemFromList(event.index, 'announcement', event.id);
  }

  protected onAnnouncementResponded(event: { index: number; id: string }) {
    this.removeItemFromList(event.index, 'announcement', event.id);
  }

  protected onSurveyCompleted() {
    this.removeItemFromList(0, 'survey', this.surveys.items[0].survey._id);
  }

  protected onSurveySkipped() {
    this.removeItemFromList(0, 'survey', this.surveys.items[0].survey._id);
  }

  protected removeItemFromList(
    index: number,
    type: 'announcement' | 'recognition' | 'survey' | 'recognitionReminder',
    itemId: string
  ) {
    const selector = itemId
      ? `.whats-new-item--${type}[data-id="${itemId}"]`
      : `.whats-new-item--${type}[data-index="${index}"]`;

    const article = document.querySelector(selector) as HTMLElement | null;

    if (!article) {
      this._whatsNewService.removeItemFromTheList(itemId, type);
      return;
    }

    const remove = () => {
      article.remove();
      this._whatsNewService.removeItemFromTheList(itemId, type);
    };

    article.classList.add('whats-new-item--removed');

    article.addEventListener('animationend', remove, { once: true });
    article.addEventListener('webkitAnimationEnd', remove, { once: true });
  }

  protected onLogout() {
    this._authenticationService.logout({
      navigateTo: 'login'
    });
    this._modalCtrl.dismiss(null, 'confirm', this._modalName);
  }

  protected onAnnouncementPreviewClose() {
    this.selectedAnnouncementIndex = -1;

    setTimeout(() => {
      this.selectedAnnouncement = null;
    }, 500);
  }

  protected onSurveyPreviewClose() {
    this.selectedSurvey = null;
  }

  protected trackRecognitionsBy(_index: number, recognition: LedgerModel) {
    return recognition._id.toString();
  }

  protected trackAnnouncementsBy(
    _index: number,
    announcement: AnnouncementModel
  ) {
    return announcement._id;
  }

  protected trackRemindersBy(_index: number, reminder: { title: string }) {
    return reminder.title;
  }

  protected trackSurveysBy(
    _index: number,
    survey: {
      survey: MongoStoredObject<IPsychologicalSurvey>;
      questions: MongoStoredObject<IPsychologicalSurveyQuestion>[];
    }
  ) {
    return survey.survey._id;
  }

  protected async whatsNewItemButtonClick(buttonId: string) {
    switch (buttonId) {
      case 'send-recognition':
        this.showRecognitionModal();
        break;
      case 'dismiss':
        this.dismissRecognitionReminder();
        break;
    }
  }

  protected async dismissRecognitionReminder() {
    const loading = await this._loadingCtrl.create({
      message: 'Dismissing...'
    });
    loading.present();
    this._recognitionApiService.dismissRecognitionReminder().subscribe(() => {
      loading.dismiss();
      this.removeItemFromList(0, 'recognitionReminder', '');
    });
  }

  protected async showRecognitionModal() {
    const { recognition, success } = await showEditRecognitionModal(
      {
        createMode: true
      },
      this._modalCtrl,
      this._actionSheetCtrl
    );

    if (success) {
      const loading = await this._loadingCtrl.create({
        message: 'Posting...'
      });
      loading.present();

      this._recognitionApiService
        .postRecognition({
          body: {
            recipientId: recognition.userId,
            behaviorId: recognition.behaviorId,
            description: recognition.description,
            isPrivate: recognition.isPrivate
          }
        })
        .subscribe({
          next: () => {
            loading.dismiss();
            this.removeItemFromList(0, 'recognitionReminder', '');
          },
          error: (e: { error: { message: string } }) => {
            loading.dismiss();
            this._toastService.presentToast(
              e.error?.message || 'There was an error creating your post'
            );
          }
        });
    }
  }
}
